import type { Theme as DataTheme } from "@glideapps/glide-data-grid";
import { systemFontFamily } from "common/BaseTheme";
import type { GlobalStyleComponent, Interpolation, ThemedStyledInterface, ThemedStyledProps } from "styled-components";
import baseStyled, { createGlobalStyle as createGlobalStyleUntyped } from "styled-components";
import type { DeepPartial } from "utility-types";
import { lookupValue } from "webapp/lib/theme-util";
import { fontStyles } from "webapp/lib/theme/font-styles";
import { initFeatureFlagsWithStorage } from "../lib/init-feature-flags";
import { mergeTheme } from "./lib/theme-utils";

const defaultFontSize = 16;
const defaultFontWeight = "normal";
const defaultFontFamily = systemFontFamily;

const heavyFontWeight = "bold";
const ultraHeavyFontWeight = "900";

const smallShadow = "0px 3px 4px rgba(0, 0, 0, 0.08)";
const largeShadow = "0px 12px 32px rgba(0, 0, 0, 0.2)";

const hoverTransition = "0.1s";

const shadowColor = (opacity: number) => `rgba(62, 65, 86, ${opacity})`;
const shadowColorDark = (opacity: number) => `rgba(0, 0, 0, ${opacity})`;

export const coreTheme = {
    // ----------------------------------------------
    // VNEXT - foundation colors (TODO: Move this in separate file?)
    //
    // 1. Neutrals
    n0: "#FFFFFF",
    n0A: "rgba(255,255,255,0)",
    n50: "#FAFAFB",
    n100: "#F7F7F8",
    n100A: "rgba(121,121,131,0.06)",
    n200: "#F2F2F3",
    n200A: "rgba(121,121,131,0.1)",
    n300: "#E8E8EB",
    n300A: "rgba(121,121,131,0.16)",
    n400: "#E0E0E4",
    n400A: "rgba(115,116,131,0.22)",
    n500: "#B9B9C1",
    n500A: "rgba(62,65,86,0.37)",
    n600: "#92929D",
    n600A: "rgba(62,65,86,0.58)",
    n700: "#606174",
    n700A: "rgba(54,55,79,0.8)",
    n800: "#323241",
    n800A: "rgba(29,29,47,0.92)",
    n900: "#070D10",

    //
    // 2. Colors
    // blue
    b100: "rgba(62,116,253,0.1)",
    b200: "rgba(58,101,253,0.15)",
    b300: "rgba(79,118,255,0.5)",
    b400: "#4F5DFF", // default
    b500: "#2D3DED",

    // teal
    t100: "rgba(0, 156, 166, 0.1)",
    t200: "rgba(0, 156, 166, 0.2)",
    t300: "rgba(0, 156, 166, 0.5)",
    t400: "#009CA6",
    t500: "#007488",

    // green
    g100: "rgba(0,196,83,0.1)",
    g200: "rgba(0,196,83,0.15)",
    g300: "rgba(0,196,83,0.5)",
    g400: "#00C453", // default
    g500: "#00A851",

    // red
    r100: "rgba(255,68,65,0.1)",
    r200: "rgba(255,68,65,0.15)",
    r300: "rgba(255,68,65,0.5)",
    r400: "#FF4441", // default
    r500: "#E42723",

    // yellow
    y100: "rgba(255,185,0,0.1)",
    y200: "rgba(255,185,0,0.15)",
    y300: "rgba(255,185,0,0.5)",
    y400: "#FFB900", // default
    y500: "#E78C00",

    // purple
    p100: "rgba(212,33,180,0.1)",
    p200: "rgba(212,33,180,0.15)",
    p300: "rgba(212,33,180,0.5)",
    p400: "#D421B4", // default
    p500: "#AF008E",

    accent: "#005844",

    //
    // 3. Others (same for light & dark)
    // components
    component1: "#1EDA5B",
    component2: "#00CD92",
    component3: "#0076F3",
    component4: "#6A5EE6",
    component5: "#B458DB",
    component6: "#EE389D",
    component7: "#FF2C6A",
    component8: "#FF4E4B",

    // brand
    brandAqua: "#12CCE5",
    brandLavender: "#887EF9",
    brandGradient: "radial-gradient(100% 300% at 0% 100%, #64D851 0%, #16B7B2 50%, #3C5DFF 120%), #00C453",

    // New brand colors
    newBrand1: "#17B5CB",
    newBrand2: "#FF7A56",
    newBrand3: "#6C62E3",
    newBrand4: "#007D8E",
    newBrand5: "#FFB11B",
    newBrand6: "#7C8AA1",
    newBrand7: "#EE8BAE",
    newBrandGradient: "linear-gradient(90deg, #12CCE5 25.62%, #887EF9 74.07%);",

    // StatusBar color used for <meta> theme-color
    OSstatusBar: "#fff",

    //
    // VNEXT - builder overlay
    //
    textDark: "_n900",
    textBase: "_n800A",
    textPale: "_n700A",
    textXpale: "_n600A",
    textDisabled: "_n500A",
    textInverse: "_n0",
    textSuccess: "_g500",
    textWarning: "_y500",
    textDanger: "_r500",
    textAccent: "_b500",

    textContextualDark: "_n900",
    textContextualBase: "_n800A",
    textContextualPale: "_n700A",
    textContextualXpale: "_n600A",
    textContextualDisabled: "_n500A",
    textContextualInverse: "_n0",
    textContextualSuccess: "_g500",
    textContextualWarning: "_y500",
    textContextualDanger: "_r500",
    textContextualAccent: "_b500",

    iconDark: "_n800A",
    iconBase: "_n700A",
    iconPale: "_n600A",
    iconXpale: "_n500A",
    iconDisabled: "_n400A",
    iconInverse: "_n0",
    iconSuccess: "_g400",
    iconWarning: "_y400",
    iconDanger: "_r400",

    bgFronter: "_n0",
    bgFront: "_n0",
    bgMiddle: "_n100",
    bgMiddleTransparent: "rgba(249, 249, 250, .82)",
    bgBack: "_n200",
    bgBehind: "_n300",

    bgContainerBase: "_n50",
    bgContainerHighlight: "_n0",

    bgHovered: "_n100A",

    borderDark: "_n400A",
    borderBase: "_n300A",
    borderPale: "_n200A",
    borderXpale: "_n100A",

    separatorColor: "_n200A",

    shadowSoft0: `0px 0px 1px ${shadowColor(0.25)}`,
    shadowSoft1: `0px 0px 1px ${shadowColor(0.25)}, 0px 1px 2.5px ${shadowColor(0.1)}`,
    shadowSoft2: `0px 0px 1px ${shadowColor(0.25)}, 0px 4px 8px ${shadowColor(0.1)}`,
    shadowSoft3: `0px 0px 1px ${shadowColor(0.25)}, 0px 6px 12px ${shadowColor(0.1)}`,
    shadowSoft4: `0px 0px 1px ${shadowColor(0.25)}, 0px 12px 24px ${shadowColor(0.15)}`,
    shadowSoft5: `0px 0px 1px ${shadowColor(0.25)}, 0px 12px 24px ${shadowColor(0.15)}`,
    shadowSoft6: `0px 0px 1px ${shadowColor(0.25)}, 0px 12px 24px ${shadowColor(0.15)}`,

    shadow0: `0px 0px 1px ${shadowColor(0.25)}`,
    shadow1: `0px 0px 1px ${shadowColor(0.25)}, 0px 1px 2.5px ${shadowColor(0.1)}`,
    shadow2: `0px 0px 1px ${shadowColor(0.25)}, 0px 4px 8px ${shadowColor(0.1)}`,
    shadow3: `0px 0px 1px ${shadowColor(0.25)}, 0px 6px 12px ${shadowColor(0.1)}`,
    shadow4: `0px 0px 1px ${shadowColor(0.25)}, 0px 12px 24px ${shadowColor(0.15)}`,
    shadow5: `0px 0px 1px ${shadowColor(0.25)}, 0px 12px 24px ${shadowColor(0.15)}`,
    shadow6: `0px 0px 1px ${shadowColor(0.25)}, 0px 12px 24px ${shadowColor(0.15)}`,

    shadowDark0: `0px 0px 1px ${shadowColor(0.375)}, 0px 0px 1px ${shadowColor(0.2)}`,
    shadowDark1: `0px 0px 1px ${shadowColor(0.375)}, 0px 1px 3px ${shadowColor(0.2)}`,
    shadowDark2: `0px 0px 1px ${shadowColor(0.375)}, 0px 4px 8px ${shadowColor(0.2)}`,
    shadowDark3: `0px 0px 1px ${shadowColor(0.375)}, 0px 6px 12px ${shadowColor(0.2)}`,
    shadowDark4: `0px 0px 1px ${shadowColor(0.375)}, 0px 12px 24px ${shadowColor(0.2)}, 0px 20px 40px ${shadowColor(
        0.1
    )}`,
    shadowDark5: `0px 0px 1px ${shadowColor(0.375)}, 0px 12px 24px ${shadowColor(0.2)}, 0px 20px 40px ${shadowColor(
        0.1
    )}`,
    shadowDark6: `0px 0px 1px ${shadowColor(0.375)}, 0px 12px 24px ${shadowColor(0.2)}, 0px 20px 40px ${shadowColor(
        0.1
    )}`,

    shadowXdark0: `0px 0px 1px ${shadowColor(0.375)}, 0px 0px 1px ${shadowColor(0.2)}`,
    shadowXdark1: `0px 0px 1px ${shadowColor(0.375)}, 0px 1px 3px ${shadowColor(0.2)}`,
    shadowXdark2: `0px 0px 1px ${shadowColor(0.375)}, 0px 4px 8px ${shadowColor(0.2)}`,
    shadowXdark3: `0px 0px 1px ${shadowColor(0.375)}, 0px 6px 12px ${shadowColor(0.2)}`,
    shadowXdark4: `0px 0px 1px ${shadowColor(0.375)}, 0px 12px 24px ${shadowColor(0.2)}, 0px 20px 40px ${shadowColor(
        0.1
    )}`,
    shadowXdark5: `0px 0px 1px ${shadowColor(0.375)}, 0px 12px 24px ${shadowColor(0.2)}, 0px 20px 40px ${shadowColor(
        0.1
    )}`,
    shadowXdark6: `0px 0px 1px ${shadowColor(0.375)}, 0px 12px 24px ${shadowColor(0.2)}, 0px 20px 40px ${shadowColor(
        0.1
    )}`,

    shadowPanelsColor: "rgba(49, 49, 57, 0.05)",
    shadowPanelsColorDarker: "rgba(0, 0, 0, 0.08)",

    transitionBase: "0.125s ease",
    transitionSlow: "0.25s ease",
    transitionFast: "0.1s ease",
};

export type CoreTheme = typeof coreTheme;

// Light theme
const builderThemeBase = {
    overlayName: "LightTheme",

    ...coreTheme,

    accentPrimary: "_b400",
    accentSecondary: "#12CCE5",
    accentTertiary: "_n200A",
    accentInverse: "_n900",
    accentGradient: "_brandGradient",
    accentSuccess: "_g400",
    accentWarning: "_y400",
    accentDanger: "_r400",

    formControlBg: "_n200A",
    formControlBgHover: "_n300A",
    formControlRadius: "8px",

    iconDuotone: {
        primary: "_n900",
        secondary: "_n500A",
        primarySelected: "_b400",
        secondarySelected: "_b300",
    },

    modals: {
        backgroundTint: "_n500A",
    },

    // ----------------------------------------------
    // VPREVIOUS (to deprecate)
    // system colors
    darkGrey: "_n700",
    layoutTint: "_b400",
    dataTint: "_g400",
    settingsTint: "_n700",

    // kill these
    blue: "_b400",
    blueDark: "_b500",
    blueTransparent: "_b100",
    bgGreen: "_g400",
    // end kill list

    fgColorOpaque: "_n800",
    fgColorDarkest: "_n900",
    fgColorDark: "_n800A",
    fgColorMedium: "_n700A",
    fgColorLight: "_n600A",
    fgColorAccent: "_n0",

    bgColorLight: "_bgFront",
    bgColorLightTransparent: "_n100A",
    bgColorMedium: "_bgBack",
    bgColorMediumTransparent: "_n200A",
    bgColorAccent: "_n800",
    bgColorAccentTransparent: "_n800A",
    bgColorDark: "_n900",

    borderColor: "_borderBase",
    borderColorLight: "_borderPale",

    bgColorAlt: "_bgBack",
    bgColorAltLight: "_bgBack",

    acceptColor: "_accentPrimary",
    cancelColor: "_accentTertiary",
    destroyColor: "_accentDanger",
    errorColor: "_accentDanger",
    warningColor: "_accentWarning",

    white: "white",

    heavyFontWeight,
    ultraHeavyFontWeight,

    headerFontSize: "40px",
    headerWeight: "bold",
    headerFontFamily: defaultFontFamily,

    subheaderFontSize: "40px",
    subheaderWeight: defaultFontWeight,
    subheaderFontFamily: defaultFontFamily,

    labelFontWeight: defaultFontWeight,
    labelFontSize: `${defaultFontSize}px`,
    labelFontSizeSmall: `${Math.round(defaultFontSize * 0.875)}px`,
    labelFontFamily: defaultFontFamily,

    iconShadow: smallShadow,
    modalShadow: largeShadow,

    linkColor: "_acceptColor",
    linkHoverConnectColor: "_acceptColor",

    hoverTransition,

    fontStyles,

    dashboard: {
        bgCard: "_bgFront",
        bgCardHover: "_bgFront",
    },

    conditionContainer: {
        bgColor: "_bgColorLight",
        bgInner: "_bgColorAlt",
    },

    dropdownButton: {
        borderNormal: "_n0A",
        borderOpen: "_n0A",
        bgColor: "_formControlBg",
        bgHover: "_formControlBgHover",
        bgActive: "_formControlBgHover",
    },

    inlineDropdownButton: {
        borderNormal: "_fgColorLight",
        borderOpen: "#313139",
        bgColor: "_bgColorLight",
        bgActive: "_bgColorMedium",
    },

    dropdown: {
        iconFg: "white",
        bgSelected: "_bgColorMedium",
        bg: "_bgFront",
    },

    buttonTile: {
        newFg: "_acceptColor",
        newBg: "_blueTransparent",
        videoFg: "#ffffff",
        videoBg: "_bgGreen",
    },

    badge: {
        nonProBackground: "_bgGreen",
        proBackground: "_bgColorAccent",
    },

    button: {
        bgHover: "_bgColorMedium",
        fgAccept: "_fgColorAccent",
    },

    buttonOutline: {
        bgColor: "_n0",
        bgHover: "_n0",
        shadow: "_shadow1",
        shadowHover: "_shadowDark1",
    },

    scrollbar: {
        color: "_borderColor",
        thumb: "_fgColorLight",
    },

    dataViewer: {
        accentColor: "#4F5DFF",
        accentLight: "rgba(62, 116, 253, 0.1)",

        textDark: lookupValue("textBase"),
        textMedium: lookupValue("textPale"),
        textLight: lookupValue("textXpale"),
        textBubble: lookupValue("textBase"),

        bgIconHeader: lookupValue("n700"),
        fgIconHeader: lookupValue("n0"),
        textHeader: lookupValue("textBase"),
        textGroupHeader: lookupValue("textXpale"),
        textHeaderSelected: lookupValue("n0"),

        bgCell: lookupValue("bgFront"),
        bgCellMedium: lookupValue("n50"),
        bgHeader: lookupValue("bgMiddle"),
        bgHeaderHasFocus: lookupValue("n300"),
        bgHeaderHovered: lookupValue("n100A"),

        bgBubble: lookupValue("bgBack"),
        bgBubbleSelected: lookupValue("bgFront"),

        bgSearchResult: "#fff9e3",

        borderColor: lookupValue("borderColor"),
        drilldownBorder: "rgba(0, 0, 0, 0)",

        linkColor: "#4F5DFF",
    } as DataTheme,

    leftPanelButton: {
        fgColor: "_fgColorMedium",
        fgActive: "_fgColorDark",
    },

    glideCircle: {
        bgColor: "#082129",
    },

    dragItem: {
        bgColor: "transparent",
        bgColorDragging: "_fgColorAccent",
        bgTop: "transparent",
        bgBottom: "_bgColorMedium",
    },

    sectionTabBar: {
        bgColor: "_formControlBg",
        item: {
            fgColor: "_textPale",
            fgColorHover: "_textBase",
            fgColorSelected: "_textBase",
            bgColorHover: "_n100A",
            bgColorSelected: "_n0",
        },
    },

    enumPicker: {
        // READY TO KILL
        fgColor: "_fgColorDark",
        fgColorSelected: "_fgColorDark",
        fgColorHover: "_fgColorDark",
        bgColorSelected: "_blueTransparent",
        bgColorHover: "_bgColorMedium",
        iconColor: "_fgColorMedium",
        iconSelectedColor: "_acceptColor",
        iconColorHover: "_fgColorDark",
    },

    icon: {
        fgColor: "_fgColorAccent",
        bgColor: "_fgColorMedium",
        accentColor: "_fgColorLight",
    },

    componentIcon: {
        containerColor: "_bgColorAccent",
        fgColor: "_fgColorDarkest",
        bgColor: "_bgColorLight",
    },

    colorPicker: {
        warningBgColor: "_bgColorLight",
    },

    switchToggle: {
        background: "_n400",
    },

    upgradeModal: {
        lockColor: "_n900",
        border: "_n400",
        basic: {
            base: "_g400",
            gradientLighter: "rgba(0, 196, 83, 0.1)",
            gradientLightest: "rgba(0, 196, 83, 0.04)",
        },
        pro: {
            base: "_b400",
            gradientLighter: "rgba(79, 118, 255, 0.1)",
            gradientLightest: "rgba(79, 118, 255, 0.04)",
        },
        privatePro: {
            base: "_n900",
            gradientLighter: "rgba(7, 13, 16, 0.1)",
            gradientLightest: "rgba(7, 13, 16, 0.04)",
        },
        plans: {
            panelHover: "_bgMiddle",
        },
        planPreview: {
            background: "_n100",
        },
        promoCode: {
            background: "_n0",
        },
        planConfirm: {
            color: "_textPale",
        },
    },
};

initFeatureFlagsWithStorage();

export type BuilderTheme = typeof builderThemeBase;
type BuilderThemeOverlay = DeepPartial<BuilderTheme> & { [s: string]: string | {} } & { overlayName: string };

export const darkCoreTheme: CoreTheme = {
    n0: "#000",
    n0A: "rgba(0,0,0,0)",
    n50: "#131315",
    n100: "#131315",
    n100A: "rgba(225,225,255,0.08)",
    n200: "#1E1E24",
    n200A: "rgba(225,225,255,0.14)",
    n300: "#2C2C33",
    n300A: "rgba(225,225,255,0.2)",
    n400: "#3D3D45",
    n400A: "rgba(225,225,255,0.27)",
    n500: "#65656D",
    n500A: "rgba(240,240,255,0.43)",
    n600: "#7C7C85",
    n600A: "rgba(240,240,255,0.52)",
    n700: "#BEBEC0",
    n700A: "rgba(252,254,255,0.75)",
    n800: "#F1F2F3",
    n800A: "rgba(252,254,255,0.93)",
    n900: "#FFF",

    //
    // 2. Colors
    // blue
    b100: "rgba(125,140,255,0.14)",
    b200: "rgba(125,140,255,0.33)",
    b300: "rgba(125,140,255,0.6)",
    b400: "#7283FF", // default
    b500: "#929FFF",

    // teal
    t100: "rgba(9, 177, 188, 0.2)",
    t200: "rgba(0, 156, 166, 0.33)",
    t300: "rgba(0, 156, 166, 0.6)",
    t400: "#009CA6",
    t500: "#36BFC8",

    // green
    g100: "rgba(0,201,107,0.16)",
    g200: "rgba(0,201,107,0.3)",
    g300: "rgba(0,201,107,0.6)",
    g400: "#00CB5E", // default
    g500: "#68E7A2",

    // red
    r100: "rgba(249,69,66,0.2)",
    r200: "rgba(249,69,66,0.33)",
    r300: "rgba(249,69,66,0.6)",
    r400: "#F94542", // default
    r500: "#FF7674",

    // yellow
    y100: "rgba(250,183,0,0.2)",
    y200: "rgba(250,183,0,0.33)",
    y300: "rgba(250,183,0,0.6)",
    y400: "#FAB700", // default
    y500: "#FFD666",

    // purple
    p100: "rgba(210,57,182,0.25)",
    p200: "rgba(210,57,182,0.33)",
    p300: "rgba(210,57,182,0.6)",
    p400: "#D239B6", // default
    p500: "#F182DE",

    accent: "#005844",

    //
    // 3. Others (same for light & dark)
    // components
    component1: "#1EDA5B",
    component2: "#00CD92",
    component3: "#0076F3",
    component4: "#6A5EE6",
    component5: "#B458DB",
    component6: "#EE389D",
    component7: "#FF2C6A",
    component8: "#FF4E4B",

    // brand
    brandAqua: "#12CCE5",
    brandLavender: "#887EF9",
    brandGradient: "radial-gradient(100% 550% at 0% 100%, #64D851 0%, #16B7B2 60%, #3C5DFF 100%), #00C453",

    // New brand colors
    newBrand1: "#17B5CB",
    newBrand2: "#FF7A56",
    newBrand3: "#6C62E3",
    newBrand4: "#007D8E",
    newBrand5: "#FFB11B",
    newBrand6: "#7C8AA1",
    newBrand7: "#EE8BAE",
    newBrandGradient: "linear-gradient(90deg, #12CCE5 25.62%, #887EF9 74.07%);",

    OSstatusBar: "#000",

    //
    // VNEXT - builder overlay
    //
    textDark: "_n900",
    textBase: "_n800A",
    textPale: "_n700A",
    textXpale: "_n600A",
    textDisabled: "_n500A",
    textInverse: "_n0",
    textSuccess: "_g500",
    textWarning: "_y500",
    textDanger: "_r500",
    textAccent: "_b500",

    textContextualDark: "_n900",
    textContextualBase: "_n800A",
    textContextualPale: "_n700A",
    textContextualXpale: "_n600A",
    textContextualDisabled: "_n500A",
    textContextualInverse: "_n0",
    textContextualSuccess: "_g500",
    textContextualWarning: "_y500",
    textContextualDanger: "_r500",
    textContextualAccent: "_b500",

    iconDark: "_n800",
    iconBase: "_n700A",
    iconPale: "_n600A",
    iconXpale: "_n500A",
    iconDisabled: "_n400A",
    iconInverse: "_n0",
    iconSuccess: "_g400",
    iconWarning: "_y400",
    iconDanger: "_r400",

    bgFronter: "_n400",
    bgFront: "_n300",
    bgMiddle: "_n200",
    bgMiddleTransparent: "rgba(33, 33, 41, .82)",
    bgBack: "_n100",
    bgBehind: "_n0",

    bgContainerBase: "_n100",
    bgContainerHighlight: "_n300",

    bgHovered: "_n200A",

    borderDark: "_n400A",
    borderBase: "_n200A",
    borderPale: "_n200A",
    borderXpale: "_n100A",

    separatorColor: "_n100A",

    shadowSoft0: `0px 0px 1px ${shadowColorDark(0.25)}`,
    shadowSoft1: `0px 0px 1px ${shadowColorDark(0.25)}, 0px 1px 2.5px ${shadowColorDark(0.1)}`,
    shadowSoft2: `0px 0px 1px ${shadowColorDark(0.25)}, 0px 4px 8px ${shadowColorDark(0.1)}`,
    shadowSoft3: `0px 0px 1px ${shadowColorDark(0.25)}, 0px 6px 12px ${shadowColorDark(0.1)}`,
    shadowSoft4: `0px 0px 1px ${shadowColorDark(0.25)}, 0px 12px 24px ${shadowColorDark(0.15)}`,
    shadowSoft5: `0px 0px 1px ${shadowColorDark(0.25)}, 0px 12px 24px ${shadowColorDark(0.15)}`,
    shadowSoft6: `0px 0px 1px ${shadowColorDark(0.25)}, 0px 12px 24px ${shadowColorDark(0.15)}`,

    shadow0: `0px 0px 1px ${shadowColorDark(0.25)}`,
    shadow1: `inset 0 1px 0 rgb(255 255 255 / 3%), 0px 0px 1px ${shadowColorDark(
        0.25
    )}, 0px 1px 2.5px ${shadowColorDark(0.2)}`,
    shadow2: `inset 0 1px 0 rgb(255 255 255 / 3%), 0px 0px 1px ${shadowColorDark(0.25)}, 0px 4px 8px ${shadowColorDark(
        0.2
    )}`,
    shadow3: `inset 0 1px 0 rgb(255 255 255 / 3%), 0px 0px 1px ${shadowColorDark(0.25)}, 0px 6px 12px ${shadowColorDark(
        0.2
    )}`,
    shadow4: `inset 0 1px 0 rgb(255 255 255 / 3%), 0px 0px 1px ${shadowColorDark(
        0.25
    )}, 0px 12px 24px ${shadowColorDark(0.25)}`,
    shadow5: `inset 0 1px 0 rgb(255 255 255 / 3%), 0px 0px 1px ${shadowColorDark(
        0.25
    )}, 0px 12px 24px ${shadowColorDark(0.25)}`,
    shadow6: `inset 0 1px 0 rgb(255 255 255 / 3%), 0px 0px 1px ${shadowColorDark(
        0.25
    )}, 0px 12px 24px ${shadowColorDark(0.25)}`,

    shadowDark0: `inset 0 0 0 1px rgb(255 255 255 / 7%), 0px 0px 1px ${shadowColorDark(
        0.375
    )}, 0px 0px 1px ${shadowColorDark(0.3)}`,
    shadowDark1: `inset 0 0 0 1px rgb(255 255 255 / 7%), 0px 0px 1px ${shadowColorDark(
        0.375
    )}, 0px 1px 3px ${shadowColorDark(0.3)}`,
    shadowDark2: `inset 0 0 0 1px rgb(255 255 255 / 7%), 0px 0px 1px ${shadowColorDark(
        0.375
    )}, 0px 4px 8px ${shadowColorDark(0.3)}`,
    shadowDark3: `inset 0 0 0 1px rgb(255 255 255 / 7%), 0px 0px 1px ${shadowColorDark(
        0.375
    )}, 0px 6px 12px ${shadowColorDark(0.3)}`,
    shadowDark4: `inset 0 0 0 1px rgb(255 255 255 / 7%), 0px 0px 1px ${shadowColorDark(
        0.375
    )}, 0px 12px 24px ${shadowColorDark(0.3)}, 0px 20px 40px ${shadowColorDark(0.1)}`,
    shadowDark5: `inset 0 0 0 1px rgb(255 255 255 / 7%), 0px 0px 1px ${shadowColorDark(
        0.375
    )}, 0px 12px 24px ${shadowColorDark(0.3)}, 0px 20px 40px ${shadowColorDark(0.1)}`,
    shadowDark6: `inset 0 0 0 1px rgb(255 255 255 / 7%), 0px 0px 1px ${shadowColorDark(
        0.375
    )}, 0px 12px 24px ${shadowColorDark(0.3)}, 0px 20px 40px ${shadowColorDark(0.1)}`,

    shadowXdark0: `inset 0 0 0 1px rgb(255 255 255 / 7%), 0px 0px 1px ${shadowColorDark(
        0.375
    )}, 0px 0px 1px ${shadowColorDark(0.3)}`,
    shadowXdark1: `inset 0 0 0 1px rgb(255 255 255 / 7%), 0px 0px 1px ${shadowColorDark(
        0.375
    )}, 0px 1px 3px ${shadowColorDark(0.3)}`,
    shadowXdark2: `inset 0 0 0 1px rgb(255 255 255 / 7%), 0px 0px 1px ${shadowColorDark(
        0.375
    )}, 0px 4px 8px ${shadowColorDark(0.3)}`,
    shadowXdark3: `inset 0 0 0 1px rgb(255 255 255 / 7%), 0px 0px 1px ${shadowColorDark(
        0.375
    )}, 0px 6px 12px ${shadowColorDark(0.3)}`,
    shadowXdark4: `inset 0 0 0 1px rgb(255 255 255 / 7%), 0px 0px 1px ${shadowColorDark(
        0.375
    )}, 0px 12px 24px ${shadowColorDark(0.2)}, 0px 20px 40px ${shadowColorDark(0.1)}`,
    shadowXdark5: `inset 0 0 0 1px rgb(255 255 255 / 7%), 0px 0px 1px ${shadowColorDark(
        0.375
    )}, 0px 12px 24px ${shadowColorDark(0.2)}, 0px 20px 40px ${shadowColorDark(0.1)}`,
    shadowXdark6: `inset 0 0 0 1px rgb(255 255 255 / 7%), 0px 0px 1px ${shadowColorDark(
        0.375
    )}, 0px 12px 24px ${shadowColorDark(0.2)}, 0px 20px 40px ${shadowColorDark(0.1)}`,

    shadowPanelsColor: "rgba(0,0,0, 0.25)",
    shadowPanelsColorDarker: "rgba(0, 0, 0, 0.33)",

    transitionBase: "0.125s ease",
    transitionSlow: "0.25s ease",
    transitionFast: "0.1s ease",
};

const darkThemeOverlay: BuilderThemeOverlay = {
    overlayName: "DarkTheme",

    ...darkCoreTheme,

    iconDuotone: {
        primary: "_n900",
        secondary: "_n600A",
        primarySelected: "_b400",
        secondarySelected: "_b300",
    },

    modals: {
        backgroundTint: "rgba(0,0,0, 0.5)",
    },

    // ----------------------------------------------
    // VPREVIOUS (to deprecate)
    settingsTint: "#FFFFFF",

    fgColorOpaque: "_n800",
    fgColorDarkest: "_n900",
    fgColorDark: "_n800A",
    fgColorMedium: "_n700A",
    fgColorLight: "_n600A",
    fgColorAccent: "_n0",

    bgColorLight: "_bgFront",
    bgColorLightTransparent: "_n100A",
    bgColorMedium: "_bgBack",
    bgColorMediumTransparent: "_n200A",
    bgColorAccent: "_n800",
    bgColorAlt: "_bgBack",
    bgColorAltLight: "_bgBack",

    borderColor: "_borderBase",
    borderColorLight: "_borderPale",
    blue: "_accentPrimary",
    acceptColor: "_accentPrimary",
    cancelColor: "_accentTertiary",

    // this overlay only
    bgColorLighter: "#3D3E47",
    bgColorLightest: "#4A4A56",

    dashboard: {
        bgCard: "_bgFront",
        bgCardHover: "_n400",
    },

    conditionContainer: {
        bgColor: "_bgColorLight",
        bgInner: "_bgColorLighter",
    },

    glideCircle: {
        bgColor: "#000000",
    },

    button: {
        bgHover: "_bgColorLightest",
        fgAccept: "_fgColorDark",
    },

    buttonOutline: {
        bgColor: "_n200A",
        bgHover: "_n300A",
        shadow: "none",
        shadowHover: "none",
    },

    dropdownButton: {
        borderNormal: "_n0A",
        borderOpen: "_n0A",
        bgColor: "_formControlBg",
        bgActive: "_formControlBgHover",
    },

    inlineDropdownButton: {
        borderNormal: "_fgColorLight",
        borderOpen: "#313139",
        bgColor: "_bgColorLightest",
        bgActive: "_bgColorLightest",
    },

    dropdown: {
        iconFg: "_bgColorLighter",
        bgSelected: "_n100A",
        bg: "_n400",
    },

    dataViewer: {
        accentColor: "#4F5DFF",
        accentLight: "rgba(62, 116, 253, 0.1)",

        textDark: lookupValue("textDark"),
        textMedium: lookupValue("textBase"),
        textLight: lookupValue("textPale"),
        textBubble: lookupValue("textDark"),

        bgIconHeader: lookupValue("n700A"),
        fgIconHeader: lookupValue("n200"),
        textHeader: lookupValue("textBase"),
        textGroupHeader: lookupValue("textXpale"),
        textHeaderSelected: "#FFFFFF",

        bgCell: "#27282F",
        bgCellMedium: lookupValue("n50"),
        bgHeader: lookupValue("bgColorLighter"),
        bgHeaderHasFocus: lookupValue("n300"),
        bgHeaderHovered: lookupValue("n100A"),

        bgBubble: lookupValue("bgColorLighter"),
        bgBubbleSelected: lookupValue("bgFront"),

        bgSearchResult: "#302003",

        borderColor: "rgba(255, 255, 255, 0.15)",
        drilldownBorder: "rgba(255, 255, 255, 0.15)",

        linkColor: "#4F5DFF",
    } as DataTheme,

    scrollbar: {
        thumb: "#B2B2C0A6",
    },

    leftPanelButton: {
        fgColor: "#737383",
    },

    dragItem: {
        bgColor: "_bgColorLighter",
        bgColorDragging: "_bgColorLightest",
        bgTop: "_bgColorLightest",
        bgBottom: "_bgColorLighter",
    },

    // Add my shit here
    sectionTabBar: {
        bgColor: "_bgBack",
        item: {
            fgColor: "_textBase",
            fgColorHover: "_textDark",
            fgColorSelected: "_textDark",
            bgColorHover: "_n100A",
            bgColorSelected: "_bgFront",
        },
    },

    enumPicker: {
        fgColor: "_fgColorLight",
        fgColorSelected: "_fgColorDark",
        fgColorHover: "_fgColorLight",
        bgColorSelected: "_borderColor",
        bgColorHover: "#3D3E47",
        iconColor: "_iconPale",
        iconColorHover: "_iconBase",
        iconSelectedColor: "_iconDark",
    },

    icon: {
        fgColor: "_bgColorLight",
        bgColor: "_fgColorMedium",
        accentColor: "_bgColorLightest",
    },

    componentIcon: {
        containerColor: "_bgColorAccent",
        fgColor: "_fgColorDarkest",
        bgColor: "_bgColorLight",
    },

    colorPicker: {
        warningBgColor: "_bgColorAccent",
    },

    switchToggle: {
        background: "_n500",
    },

    upgradeModal: {
        lockColor: "_n300",
        border: "_borderPale",
        privatePro: {
            base: "_n0",
            gradientLighter: "rgba(255, 255, 255, 0.1)",
            gradientLightest: "rgba(255, 255, 255, 0.04)",
        },
        plans: {
            panelHover: "_n400",
        },
        planPreview: {
            background: "_n400",
        },
        promoCode: {
            background: "_n400",
        },
        planConfirm: {
            color: "_textBase",
        },
    },
};

export const styled = baseStyled as ThemedStyledInterface<BuilderTheme>;
export const coreStyled = baseStyled as ThemedStyledInterface<CoreTheme>;

export function createGlobalStyle<P = {}>(
    strings: TemplateStringsArray,
    ...interpolations: Array<Interpolation<ThemedStyledProps<P, BuilderTheme>>>
): GlobalStyleComponent<P, BuilderTheme> {
    // sorry for the as any's, TS Server wont shuttup about this one, it's just wrong
    return createGlobalStyleUntyped(strings, ...(interpolations as any)) as any;
}

export function getBuilderTheme(useDarkTheme: boolean): BuilderTheme {
    const result = mergeTheme(builderThemeBase, useDarkTheme ? [darkThemeOverlay] : []);
    return result;
}
